import * as THREE from "../../../libs/three.js/build/three.module.js";
import {DigitizationCategories, DigitizationTools} from "../ToolRegistry.js";
import {FeatureVisual} from "../FeatureVisual.js";
import {createFeature} from "../Feature.js";
import {createStyledLabel} from "../DigitizationStyle.js";
import {setLabelWanted, setLabelContent} from "../labels/labelState.js";
import {computeScreenSpaceScale, ScreenSpaceSizes} from "../ScreenSpaceScale.js";
import {PrecisionMeasure} from "../../modules/PrecisionMeasure/PrecisionMeasure.js";
import {LASER_LEG_KEYS} from "../../modules/ThreeAxisLaser/ThreeAxisLaser.js";
import {formatLength} from "./distanceTool.js";

import {t, T} from "../i18n/index.js";
// "Måling mod model": three drawing tools that measure against IFC/OBJ
// models - 3-akset laser, Punkt til nærmeste flade (vinkelret) and Flade til
// punkt (vinkelret).
//
// The interaction (live preview, vertex/edge snapping, click handling) lives
// in modules/PrecisionMeasure/PrecisionMeasure.js; THIS file is only the
// registration side: every finished measurement becomes an ordinary
// Feature + FeatureVisual, so it appears in Registreringer (under "Målinger")
// with a name, label toggle, visibility checkbox and delete button, and is
// saved/exported like every other drawing-tool result.
//   point-face / face-point -> a 2-vertex LineString, with its distance as the
//                              segment label (same look as "Mål afstand")
//   laser                   -> a Point at the laser's origin, with the six legs
//                              drawn by LaserAuxiliaryVisual (the same
//                              userData.auxiliaryVisual convention "Mål højde"
//                              uses) and every leg's distance stored in the
//                              feature's attributes

DigitizationCategories.register({id: "measurement", name: T("Målinger"), order: 3});

const AXIS_COLORS = {x: 0xff4444, y: 0x44dd66, z: 0x4499ff};
const ROD_PIXEL_RADIUS = 1.6;
const unitRod = new THREE.CylinderBufferGeometry(1, 1, 1, 8);
const UP = new THREE.Vector3(0, 1, 0);

function measurableMeshes(viewer){
	// the host (PointView's IFC / Model section) knows which elements are
	// currently measurable (visible, not ghosted)
	if(viewer.pvIfc && viewer.pvIfc.getMeshes){
		return viewer.pvIfc.getMeshes();
	}
	// fallback (e.g. the standalone Potree page): any pickable mesh that has
	// IFC-style feature edges
	return (viewer.scene.pickableMeshes || []).filter(m => m.userData && m.userData.edges);
}

function engineFor(dm){
	if(!dm._precisionMeasure){
		dm._precisionMeasure = new PrecisionMeasure(dm.viewer, {
			external: true,
			getMeshes: () => measurableMeshes(dm.viewer),
			isPaused: () => dm.isPaused(),
			shouldIgnoreClick: () => dm._consumeCopyClickGuard(),
			onStatus: (text) => {
				if(text && dm.activeTool){
					dm._showInstruction(dm.activeTool.name, text);
				}
			},
			onCommit: (measurement) => commitMeasurement(dm, measurement),
		});
	}
	return dm._precisionMeasure;
}

function startEngine(dm, tool, mode){
	dm._showInstruction(tool.name, tool.instructions);
	engineFor(dm).start(mode);
}

// ---------------------------------------------------------------------
// Laser result visual: six coloured rods + a distance label per leg
// ---------------------------------------------------------------------

class LaserAuxiliaryVisual extends THREE.Object3D{
	constructor(origin, basis, legs){
		super();
		this.rods = []; // {mesh, start, end}
		this.labels = [];
		this.labelsVisible = true;

		for(const key of LASER_LEG_KEYS){
			const leg = legs[key];
			if(!leg) continue;
			const axis = key[0];

			const mesh = new THREE.Mesh(unitRod, new THREE.MeshBasicMaterial({
				color: AXIS_COLORS[axis], polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1,
			}));
			mesh.frustumCulled = false;
			this.add(mesh);
			this.rods.push({mesh, start: origin.clone(), end: leg.point.clone()});

			// created empty and given its text afterwards: createStyledLabel() sets the
			// standing font size only AFTER construction, so text passed in up front
			// would be rendered once at TextSprite's (much larger) default size
			const label = createStyledLabel("");
			setLabelContent(label, `${axis.toUpperCase()}${key[1]} ${formatLength(leg.distance)}`);
			label.position.copy(origin).lerp(leg.point, 0.5);
			this.add(label);
			this.labels.push(label);
		}
		this._refreshLabelVisibility();
	}

	setLabelsVisible(visible){
		this.labelsVisible = visible;
		this._refreshLabelVisibility();
	}

	_refreshLabelVisibility(){
		for(const label of this.labels){
			setLabelWanted(label, this.labelsVisible);
		}
	}
	// Labels, som LabelManager (labels/) styrer: hjælpelabels udfases tidligt og vises kun ved valg/nærzoom (LABEL-STRATEGI.md 12.4)
	auxLabels(){
		return this.labels;
	}

	// per-frame, via DigitizationModule.update()'s generic auxiliaryVisual hook:
	// constant on-screen rod thickness and label size at any zoom
	update(camera, clientWidth, clientHeight){
		const dir = new THREE.Vector3();
		const mid = new THREE.Vector3();
		for(const rod of this.rods){
			dir.subVectors(rod.end, rod.start);
			const length = dir.length();
			if(length < 1e-6){
				rod.mesh.visible = false;
				continue;
			}
			mid.addVectors(rod.start, rod.end).multiplyScalar(0.5);
			const radius = computeScreenSpaceScale(camera, mid, camera.position, clientWidth, clientHeight, ROD_PIXEL_RADIUS);
			rod.mesh.visible = true;
			rod.mesh.position.copy(mid);
			rod.mesh.quaternion.setFromUnitVectors(UP, dir.normalize());
			rod.mesh.scale.set(radius, length, radius);
		}
		for(const label of this.labels){
			if(!label.visible) continue;
			const scale = computeScreenSpaceScale(camera, label.getWorldPosition(new THREE.Vector3()), camera.position,
				clientWidth, clientHeight, ScreenSpaceSizes.label);
			label.scale.set(scale, scale, scale);
		}
	}

	dispose(){
		for(const rod of this.rods){
			rod.mesh.material.dispose();
		}
		for(const label of this.labels){
			label.dispose();
		}
	}
}

// ---------------------------------------------------------------------
// Feature creation
// ---------------------------------------------------------------------

function round3(value){
	return Math.round(value * 1000) / 1000;
}

function nextName(dm, tool){
	return `${tool.name} ${dm.featureManager.features.length + 1}`;
}

// The finished look of a 2-point model measurement: no vertex labels and one distance label on the segment. Shared by the capture flow
// (registerLine) and the restore hook of point-to-face (the label text is a pure function of the two points). The caller shows the
// labels (setLabelsVisible) once the feature name is set.
function applyLineMeasurementRepresentation(visual){
	visual.setVertexLabelsSuppressed(true);
	visual.enableSegmentLabels((p, q) => formatLength(p.distanceTo(q)));
}

// The attribute afstand_m of a point-to-face / face-to-point measurement is DERIVED: always the rounded length of its 2-point line. It stays an
// attribute (shown in the attribute table and exported) but is never an independent source of truth: it is re-derived whenever the
// line changes (geometry_changed, e.g. a vertex drag) and normalised when a measurement is restored, so a saved measurement can never
// carry a distance that differs from its geometry. (The distance label is likewise computed from the geometry.)
function derivedDistance(visual){
	const [a, b] = visual.points;
	return a && b ? round3(a.position.distanceTo(b.position)) : null;
}

function keepDistanceAttributeInSync(dm, feature, visual){
	visual.addEventListener("geometry_changed", () => {
		const distance = derivedDistance(visual);
		const attributes = feature.properties.attributes || {};
		if(distance === null || attributes.afstand_m === distance) return;
		dm.featureManager.updateProperties(feature.id, {attributes: {...attributes, afstand_m: distance}});
	});
}

// Restore hook shared by both distance-line tools (see DigitizationModule._buildLoadedFeature): the finished measurement is the
// 2-point line with its distance label. afstand_m is derived from the line (normalise a stored value that differs, then keep it in step
// with later edits); the other attribute (type / side) cannot be derived from the geometry and is kept as stored. Nothing of the
// engine's pick/preview state (selected face, hover, helper geometry) exists on a finished measurement, so nothing else is recreated.
function decorateDistanceLine(visual, feature, dm){
	applyLineMeasurementRepresentation(visual);
	visual.setLabelsVisible(true);
	const distance = derivedDistance(visual);
	const attributes = feature.properties.attributes || {};
	if(distance !== null && attributes.afstand_m !== distance) feature.properties.attributes = {...attributes, afstand_m: distance};
	keepDistanceAttributeInSync(dm, feature, visual);
}

function registerLine(dm, tool, a, b, attributes, {deriveDistance = false} = {}){
	const visual = new FeatureVisual("LineString");
	applyLineMeasurementRepresentation(visual);
	dm.scene.add(visual);
	visual.appendAtEnd(a);
	visual.appendAtEnd(b);

	const feature = createFeature({
		geometryType: "LineString",
		coordinates: visual.toCoordinates(),
		category: tool.category,
		tool: tool.id,
		featureType: tool.featureType || tool.name,
		name: nextName(dm, tool),
		attributes,
	});
	visual.setInfo(feature.properties.name, feature.properties.description);
	// the measured number IS the result - show it from the start
	visual.setLabelsVisible(true);
	dm.registerNewFeature(feature, visual);
	if(deriveDistance) keepDistanceAttributeInSync(dm, feature, visual);
}

// The finished laser look: hidden vertex label, the six leg rods with their distance labels and the result label shown from the start.
// Shared by the capture flow and the restore hook (LaserTool.decorateLoaded), so both give the same result from the same canonical data.
function applyLaserRepresentation(visual, origin, basis, legs){
	visual.setVertexLabelsSuppressed(true);
	const aux = new LaserAuxiliaryVisual(origin, basis, legs);
	visual.add(aux);
	visual.userData.auxiliaryVisual = aux;
	visual.setLabelsVisible(true);
}

const LEG_SIGN = {"+": 1, "-": -1};

// Canonical form of the laser's axis basis (three unit vectors): needed to redraw the rods, and not derivable from the feature (a face
// laser's axes follow the clicked face).
export function laserBasisToJson(basis){
	return {x: basis.x.toArray(), y: basis.y.toArray(), z: basis.z.toArray()};
}

export function laserBasisFromJson(json){
	const vector = (v) => (Array.isArray(v) && v.length === 3 && v.every(Number.isFinite)) ? new THREE.Vector3(v[0], v[1], v[2]) : null;
	if(!json) return null;
	const x = vector(json.x), y = vector(json.y), z = vector(json.z);
	return (x && y && z) ? {x, y, z} : null;
}

// The six legs from the feature's own data: every leg's end point is origin + axis direction * measured distance (a missing leg is null).
// The measured distances are the finished result (attributes, to the millimetre); the end points are derived and not persisted.
export function laserLegsFromAttributes(origin, basis, attributes){
	const legs = {};
	for(const key of LASER_LEG_KEYS){
		const distance = attributes ? attributes[key] : null;
		if(!Number.isFinite(distance)){
			legs[key] = null;
			continue;
		}
		const direction = basis[key[0]].clone().multiplyScalar(LEG_SIGN[key[1]]);
		legs[key] = {distance, point: origin.clone().addScaledVector(direction, distance)};
	}
	return legs;
}

function registerLaser(dm, tool, m){
	const visual = new FeatureVisual("Point");
	dm.scene.add(visual);
	visual.appendAtEnd(m.origin);
	applyLaserRepresentation(visual, m.origin, m.basis, m.legs);

	const attributes = {akser: m.basisMode === "face" ? t("flade") : t("global (Ø/N/op)")};
	for(const key of LASER_LEG_KEYS){
		attributes[key] = m.legs[key] ? round3(m.legs[key].distance) : null;
	}
	for(const axis of ["x", "y", "z"]){
		const pos = m.legs[axis + "+"];
		const neg = m.legs[axis + "-"];
		if(pos && neg) attributes[`${axis}_samlet`] = round3(pos.distance + neg.distance);
	}

	const feature = createFeature({
		geometryType: "Point",
		coordinates: visual.toCoordinates(),
		category: tool.category,
		tool: tool.id,
		featureType: tool.featureType || tool.name,
		name: nextName(dm, tool),
		attributes,
		domainData: {laser: {basis: laserBasisToJson(m.basis)}},
	});
	visual.setInfo(feature.properties.name, feature.properties.description);
	visual.setLabelsVisible(true);
	dm.registerNewFeature(feature, visual);
}

function commitMeasurement(dm, m){
	if(m.type === "laser"){
		registerLaser(dm, LaserTool, m);
	}else if(m.type === "point-face"){
		registerLine(dm, PointToFaceTool, m.a, m.b, {
			afstand_m: round3(m.distance),
			type: m.perpendicular ? t("vinkelret") : t("nærmeste kant/hjørne"),
		}, {deriveDistance: true});
	}else if(m.type === "face-point"){
		registerLine(dm, FaceToPointTool, m.a, m.b, {
			afstand_m: round3(m.distance),
			side: m.signed >= 0 ? t("foran fladen") : t("bagved fladen"),
		}, {deriveDistance: true});
	}
}

// ---------------------------------------------------------------------
// Tool registrations
// ---------------------------------------------------------------------

export const LaserTool = {
	id: "digitize_measure_laser",
	name: T("3-akset laser"),
	category: "measurement",
	kind: "measurement",
	icon: "pointview-measure-laser-3-axis.svg",
	iconPath: "/icons/measure/pointview-measure-laser-3-axis.svg",
	resultGeometryType: "Point",
	tooltip: T("Seks ben (±X, ±Y, ±Z) måles fra et punkt på modellen til første flade i hver retning. Kun mod IFC/OBJ-modeller."),
	instructions: T("Peg på en flade, et hjørne eller en kant i modellen og klik. G skifter mellem flade-akser og Ø/N/op. Esc afslutter."),
	customCapture(dm, tool){
		startEngine(dm, tool, "laser");
	},
	// Restore extension point (see DigitizationModule._buildLoadedFeature): the finished laser keeps its six rods and distance labels,
	// rebuilt from the origin (the Point), the measured distances (attributes) and the axis basis (domainData.laser). Without a stored
	// basis (older local data) the point is restored plain rather than with guessed rod directions.
	decorateLoaded(visual, feature){
		const stored = feature && feature.domainData && feature.domainData.laser;
		const basis = laserBasisFromJson(stored && stored.basis);
		const first = visual.points[0];
		if(!basis || !first) return;
		const origin = first.position.clone();
		applyLaserRepresentation(visual, origin, basis, laserLegsFromAttributes(origin, basis, feature.properties.attributes));
	},
};

export const PointToFaceTool = {
	id: "digitize_measure_point_face",
	name: T("Punkt til nærmeste flade"),
	category: "measurement",
	kind: "measurement",
	icon: "pointview-measure-point-to-face.svg",
	iconPath: "/icons/measure/pointview-measure-point-to-face.svg",
	resultGeometryType: "LineString",
	tooltip: T("Klik et punkt i punktskyen - afstanden til nærmeste IFC-flade måles vinkelret."),
	instructions: T("Klik et punkt i punktskyen. Afstanden til nærmeste flade i modellen måles vinkelret. Esc afslutter."),
	customCapture(dm, tool){
		startEngine(dm, tool, "point-face");
	},
	// Restore extension point: the 2-point line (cloud point -> nearest model point); type stays as stored
	decorateLoaded: decorateDistanceLine,
};

export const FaceToPointTool = {
	id: "digitize_measure_face_point",
	name: T("Flade til punkt"),
	category: "measurement",
	kind: "measurement",
	icon: "pointview-measure-face-to-point.svg",
	iconPath: "/icons/measure/pointview-measure-face-to-point.svg",
	resultGeometryType: "LineString",
	tooltip: T("Vælg en IFC-flade og klik derefter punkter i punktskyen - afstanden måles vinkelret fra fladen."),
	instructions: T("Klik en flade i modellen, og klik derefter de punkter i punktskyen du vil måle til. Enter vælger en ny flade. Esc afslutter."),
	customCapture(dm, tool){
		startEngine(dm, tool, "face-point");
	},
	// en valgt flade = målingen er i gang (se PrecisionMeasure.hasOperationInProgress()); højreklik forlader først, når der
	// ikke er valgt en flade
	isOperationActive(dm){
		return !!(dm._precisionMeasure && dm._precisionMeasure.hasOperationInProgress());
	},
	// Restore extension point: the 2-point line (face foot point -> cloud point); the selected face is construction state and is not
	// persisted, side (in front of / behind the face) cannot be derived from the two points and stays as stored
	decorateLoaded: decorateDistanceLine,
};

DigitizationTools.register(LaserTool);
DigitizationTools.register(PointToFaceTool);
DigitizationTools.register(FaceToPointTool);
